import type { components } from '@tms/api-client';

import { api } from './client';
import type { ApiResult } from './identity';
import { readFailure } from './problem';

/**
 * The ledger endpoints, one function each (`FEAT-011 §5`).
 *
 * Same shape as `identity.ts`: every call answers a result that is either `ok` or carries an `ApiFailure`,
 * so a screen never sees a thrown exception for an outcome the design expects. A dropped connection on a
 * train is one of those outcomes.
 */

/** `LED-01` in one response (`FEAT-011 §5.7`). */
export type Summary = components['schemas']['SummaryResponse'];

/** One entry, as every endpoint that returns one returns it (`FEAT-011 §5.3`). */
export type LedgerEntry = components['schemas']['TransactionResponse'];

/** A page of the ledger (`FEAT-011 §5.4`). */
export type LedgerPage = components['schemas']['ListTransactionsResponse'];

/** A purpose, as `LED-02` chooses between them (`FEAT-010 §5.1`). */
export type Category = components['schemas']['CategoryView'];

/** `FEAT-011 §5.7`. The home screen, in one round trip. */
export async function getSummary(): Promise<ApiResult<Summary>> {
  const { data, error, response } = await api.GET('/v1/me/summary');

  return data ? { ok: true, value: data } : { ok: false, failure: readFailure(error, response.status) };
}

/** What `LED-02` sends (`FEAT-011 §5.1`). */
export interface CreateEntryInput {
  /**
   * **The client's**, not the server's. A device that records an entry offline needs an identity for it
   * before a server has seen it (`STD-002 §5`), and that same value is what makes the create idempotent —
   * a retry after a dropped connection returns the entry rather than making a second one.
   */
  readonly publicId: string;
  readonly kind: 'Debit' | 'Credit';
  readonly amountMinor: number;
  readonly categoryId: string;
  readonly occurredOn?: string;
  readonly note?: string;
}

/** `FEAT-011 §5.1`. `201` for a new entry, `200` for a retry of one already made. */
export async function createEntry(input: CreateEntryInput): Promise<ApiResult<LedgerEntry>> {
  const { data, error, response } = await api.POST('/v1/transactions', { body: input as never });

  return data ? { ok: true, value: data } : { ok: false, failure: readFailure(error, response.status) };
}

/** What `LED-05` asks for (`FEAT-011 §5.4`). */
export interface LedgerFilters {
  /** `yyyy-MM`, resolved by the server as the **payday** month (`FEAT-011 §3.2`). */
  readonly month?: string;
  readonly kind?: 'Debit' | 'Credit';
  readonly cursor?: string;
  readonly limit?: number;
}

/**
 * `FEAT-011 §5.4`. One page of the ledger, newest first, each row carrying the running balance as at it.
 *
 * The cursor is opaque and comes from the previous page's `nextCursor` and nowhere else — a client that
 * built one would be depending on a shape the server is free to change.
 */
export async function getLedgerPage(filters: LedgerFilters = {}): Promise<ApiResult<LedgerPage>> {
  const { data, error, response } = await api.GET('/v1/transactions', {
    params: {
      query: {
        ...(filters.month ? { month: filters.month } : {}),
        ...(filters.kind ? { kind: filters.kind } : {}),
        ...(filters.cursor ? { cursor: filters.cursor } : {}),
        ...(filters.limit ? { limit: filters.limit } : {}),
      },
    },
  });

  return data ? { ok: true, value: data } : { ok: false, failure: readFailure(error, response.status) };
}

/** One row of `LED-07` (`FEAT-011 §5.8`). */
export type TrashedEntry = components['schemas']['TrashedTransaction'];

/**
 * `FEAT-011 §5.8`. What is in the trash, most recently deleted first, each with its countdown.
 *
 * `daysRemaining` is computed by the server from `IClock`, so it is the number the purge will act on
 * rather than one a device clock invented — a phone an hour out would otherwise show two days left on
 * something with one.
 */
export async function getTrash(): Promise<ApiResult<readonly TrashedEntry[]>> {
  const { data, error, response } = await api.GET('/v1/transactions/trash');

  return data
    ? { ok: true, value: data.items ?? [] }
    : { ok: false, failure: readFailure(error, response.status) };
}

/** `LED-07`'s "Restore all". Answers how many, because the screen has to say something after the tap. */
export async function restoreAll(): Promise<ApiResult<number>> {
  const { data, error, response } = await api.POST('/v1/transactions/trash/restore-all');

  return data
    ? { ok: true, value: data.count ?? 0 }
    : { ok: false, failure: readFailure(error, response.status) };
}

/**
 * `LED-07`'s "Empty now" — the one destructive thing a person can do to their own ledger.
 *
 * It reaches only what is already in the trash; nothing live is reachable from it (`FEAT-011 §5.8`).
 */
export async function emptyTrash(): Promise<ApiResult<number>> {
  const { data, error, response } = await api.DELETE('/v1/transactions/trash');

  return data
    ? { ok: true, value: data.count ?? 0 }
    : { ok: false, failure: readFailure(error, response.status) };
}

/** `FEAT-011 §5`. One entry, including one in the trash — `LED-07` links to detail. */
export async function getEntry(id: string): Promise<ApiResult<LedgerEntry>> {
  const { data, error, response } = await api.GET('/v1/transactions/{id}', { params: { path: { id } } });

  return data ? { ok: true, value: data } : { ok: false, failure: readFailure(error, response.status) };
}

/** What `LED-04`'s edit form sends (`FEAT-011 §5.5`). Absent means unchanged, except `rowVersion`. */
export interface UpdateEntryInput {
  /**
   * **Required.** An edit that did not say which version it was editing is one nobody can check, and
   * sending it without would quietly turn optimistic concurrency off (`FEAT-011 §3.10`).
   */
  readonly rowVersion: string;
  readonly kind?: 'Debit' | 'Credit';
  readonly amountMinor?: number;
  readonly categoryId?: string;
  readonly occurredOn?: string;
  readonly note?: string;
  /** `null` already means "unchanged" on every field, so removing a note needs a flag of its own. */
  readonly clearNote?: boolean;
}

/** `FEAT-011 §5.5`. A stale `rowVersion` is a `409` carrying the entry as it now stands. */
export async function updateEntry(id: string, input: UpdateEntryInput): Promise<ApiResult<LedgerEntry>> {
  const { data, error, response } = await api.PATCH('/v1/transactions/{id}', {
    params: { path: { id } },
    body: input as never,
  });

  return data ? { ok: true, value: data } : { ok: false, failure: readFailure(error, response.status) };
}

/** `FEAT-011 §3.11`. Moves an entry to the trash. Always `204` when it is the caller's. */
export async function deleteEntry(id: string): Promise<ApiResult<null>> {
  const { error, response } = await api.DELETE('/v1/transactions/{id}', { params: { path: { id } } });

  return response.ok ? { ok: true, value: null } : { ok: false, failure: readFailure(error, response.status) };
}

/** `FEAT-011 §3.11`. Puts it back on its original date; the running balance recalculates. */
export async function restoreEntry(id: string): Promise<ApiResult<null>> {
  const { error, response } = await api.POST('/v1/transactions/{id}/restore', {
    params: { path: { id } },
  });

  return response.ok ? { ok: true, value: null } : { ok: false, failure: readFailure(error, response.status) };
}

/** `FEAT-010 §5.1`. The fifteen shipped purposes plus this person's own, hidden ones already removed. */
export async function getCategories(kind?: 'Debit' | 'Credit'): Promise<ApiResult<readonly Category[]>> {
  const { data, error, response } = await api.GET('/v1/categories', {
    params: { query: kind ? { kind } : {} },
  });

  return data
    ? { ok: true, value: data.items ?? [] }
    : { ok: false, failure: readFailure(error, response.status) };
}
